import { useEffect, useRef, useState, type FormEvent } from 'react'

type ModalOptions = { title?: string; confirmLabel?: string; danger?: boolean }
type ModalState = ModalOptions & { kind: 'confirm' | 'prompt'; message: string; value: string }

export function useModal() {
  const [dialog, setDialog] = useState<ModalState | null>(null)
  const [value, setValue] = useState('')
  const resolver = useRef<((result: string | null) => void) | null>(null)

  function open(next: ModalState) {
    setValue(next.value)
    setDialog(next)
    return new Promise<string | null>((resolve) => { resolver.current = resolve })
  }

  function close(result: string | null) {
    resolver.current?.(result)
    resolver.current = null
    setDialog(null)
  }

  useEffect(() => {
    if (!dialog) return
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') close(null) }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [dialog])

  useEffect(() => () => { resolver.current?.(null) }, [])

  function submit(event: FormEvent) {
    event.preventDefault()
    close(dialog?.kind === 'prompt' ? value : 'confirmed')
  }

  const element = dialog && <div className="app-modal-mask" onMouseDown={(event) => { if (event.target === event.currentTarget) close(null) }}>
    <section className="app-modal" role="dialog" aria-modal="true" aria-labelledby="app-modal-title">
      <h2 id="app-modal-title">{dialog.title || '请确认'}</h2>
      <p>{dialog.message}</p>
      <form onSubmit={submit}>
        {dialog.kind === 'prompt' && <input autoFocus aria-label={dialog.message} value={value} onChange={(event) => setValue(event.target.value)} />}
        <div className="app-modal-actions">
          <button type="button" onClick={() => close(null)}>取消</button>
          <button autoFocus={dialog.kind === 'confirm'} className={dialog.danger ? 'danger' : 'primary'} type="submit">{dialog.confirmLabel || '确定'}</button>
        </div>
      </form>
    </section>
  </div>

  return {
    element,
    confirm: async (message: string, options: ModalOptions = {}) => (await open({ kind: 'confirm', message, value: '', ...options })) !== null,
    prompt: (message: string, initialValue = '', options: ModalOptions = {}) => open({ kind: 'prompt', message, value: initialValue, ...options })
  }
}
